import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Loader2, Phone, RotateCcw } from "lucide-react";
import { BAND_LOOK } from "@/components/wellbeing/who5";
import { resultCardBlob } from "@/components/wellbeing/resultCard";
import { shareOrDownload } from "@/lib/gardenShare";

const Support = () => (
  <div className="mm-card p-5 border-2 border-[#F28FAD]/50" data-testid="wellbeing-support">
    <div className="font-semibold flex items-center gap-2"><Phone size={16} className="text-mm-primary" />Support is here, any time</div>
    <ul className="text-sm mt-3 space-y-1.5">
      <li>US: call or text <a href="tel:988" className="font-semibold text-mm-primary" data-testid="wellbeing-988">988</a> (Suicide &amp; Crisis Lifeline)</li>
      <li>US: text <span className="font-semibold">HOME</span> to <a href="sms:741741" className="font-semibold text-mm-primary">741741</a> (Crisis Text Line)</li>
      <li>UK &amp; Ireland: call <a href="tel:116123" className="font-semibold text-mm-primary">116 123</a> (Samaritans)</li>
      <li>Elsewhere: <a href="https://findahelpline.com" target="_blank" rel="noreferrer" className="font-semibold text-mm-primary" data-testid="wellbeing-findahelpline">findahelpline.com</a></li>
    </ul>
    <p className="text-xs text-mm-muted mt-3">If you're in immediate danger, call your local emergency number.</p>
  </div>
);

export const Result = ({ r, signedIn, onRetake }) => {
  const look = BAND_LOOK[r.band];
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { if ((await shareOrDownload(await resultCardBlob(r.band, r.label), "my-wellbeing-check.png", "My wellbeing check")) === "downloaded") toast.success("Result card saved"); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 mm-stagger" data-testid="wellbeing-result">
      <div className="rounded-[32px] p-7 sm:p-9" style={{ background: look.bg, color: look.ink }}>
        <div className="text-6xl" aria-hidden>{look.emoji}</div>
        <div className="text-sm font-semibold uppercase tracking-wider mt-4 opacity-75">Your wellbeing right now</div>
        <h2 className="text-3xl sm:text-4xl font-extrabold mt-1" data-testid="wellbeing-band">{r.label}</h2>
        <p className="mt-2 text-lg" data-testid="wellbeing-score">Score {r.score} out of 100</p>
        <p className="mt-3">{look.line}</p>
        <ul className="mt-4 space-y-2 list-disc pl-5">{look.tips.map((t) => <li key={t}>{t}</li>)}</ul>
        <div className="flex flex-wrap gap-2 mt-6">
          <button className="mm-btn !bg-white !text-[#3A2E4F]" onClick={save} disabled={busy} data-testid="wellbeing-save-card">{busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}Save result card</button>
          <button className="mm-btn-ghost" onClick={onRetake} data-testid="wellbeing-retake"><RotateCcw size={16} />Take it again</button>
        </div>
        <p className="text-xs mt-3 opacity-70">The card shows only "{r.label}", never your score or answers.</p>
      </div>
      {r.show_support && <Support />}
      {!signedIn && (
        <div className="mm-card p-6" data-testid="wellbeing-invite">
          <h3 className="text-lg font-bold">Want to see how this changes over time?</h3>
          <p className="text-mm-muted mt-1">Mindful Me helps you check in daily, spot what lifts your mood and reach for 100+ coping skills. Members can save every wellbeing check and watch the trend.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/signup" className="mm-btn" data-testid="wellbeing-signup">Create your account</Link>
            <Link to="/pricing" className="mm-btn-ghost" data-testid="wellbeing-pricing">See pricing</Link>
          </div>
        </div>
      )}
      {signedIn && r.saved && <p className="text-sm text-mm-muted" data-testid="wellbeing-saved">Saved to your account. Only you can see it.</p>}
    </div>
  );
};
